'use client';

import React, { useState, useRef, useEffect, Fragment } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/Button';
import CampaignNavigationBar from '@/components/layout/CampaignNavigationBar';
import Footer from '@/components/layout/Footer';
import NavigationBarSpacing from '@/components/layout/NavigationBarSpacing';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import {
  Rocket,
  CheckCircle,
  RefreshCw,
  BookOpen,
  ShieldCheck,
  ChevronDown,
  Check,
  Files,
  FolderSearch,
  BookOpenCheck,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';

const SIGNUP_URL = process.env.NEXT_PUBLIC_APP_URL + '/signup';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const trustBadges = [
  {
    icon: RefreshCw,
    label: 'Aggiornamento normativo continuo',
  },
  {
    icon: BookOpen,
    label: 'Fonti ufficiali sempre citate',
  },
  {
    icon: ShieldCheck,
    label: 'Risposte strutturate e verificabili',
  },
];

const howItWorks = [
  {
    step: 1,
    title: 'Inserisci il quesito',
    description: 'Descrivi il caso o la problematica in linguaggio naturale.',
  },
  {
    step: 2,
    title: 'Analisi normativa',
    description: 'Il sistema analizza normativa, prassi e fonti ufficiali aggiornate.',
  },
  {
    step: 3,
    title: 'Risposta strutturata',
    description: 'Ricevi una risposta argomentata con riferimenti verificabili.',
  },
];

const whatYouCanDos = [
  {
    icon: FolderSearch,
    title: 'Analisi Approfondita di Casi',
    description:
      'Invia il tuo caso o una bozza di atto: Trovanorma analizza le fonti ufficiali, evidenzia criticità nascoste e suggerisce strategie concrete per rafforzare la tua posizione.',
  },
  {
    icon: Files,
    title: 'Creazione e Revisione di Documenti',
    description:
      "Trovanorma genera bozze su misura in base alle tue specifiche. Puoi modificarle, confrontare le versioni e arrivare alla stesura finale perfettamente conforme e pronta per l'uso.",
  },
  {
    icon: BookOpenCheck,
    title: 'Quesiti Fiscali e Normativi',
    description:
      'In pochi secondi ottieni un parere completo e argomentato, con giurisprudenza favorevole e contraria, riferimenti diretti e link alle fonti ufficiali. Una visione a 360° per decidere o difendere con sicurezza.',
  },
];

const comparisonData = [
  {
    criterion: 'Fonte dei Dati',
    generalAI: (
      <>
        <strong>Non verificata e generica.</strong> Le informazioni provengono dal web e possono
        essere ferme a mesi o anni fa, senza garanzia di accuratezza o pertinenza legale.
      </>
    ),
    trovanorma: (
      <>
        <strong>Database proprietario e verificato.</strong> Include l’intero corpus normativo e
        giurisprudenziale italiano ed europeo, proveniente da Gazzetta Ufficiale, codici e sentenze.
      </>
    ),
  },
  {
    criterion: 'Aggiornamento dei Dati',
    generalAI: (
      <>
        <strong>Obsoleto o assente.</strong> Non recepisce in tempo reale modifiche normative o
        giurisprudenziali cruciali.
      </>
    ),
    trovanorma: (
      <>
        <strong>Sempre aggiornato.</strong> Sincronizzazione quotidiana con le fonti ufficiali per
        garantirti le informazioni più recenti.
      </>
    ),
  },
  {
    criterion: 'Citazione delle Fonti',
    generalAI: (
      <>
        <strong>Assente o inaffidabile.</strong> Fonti errate, incomplete o non funzionanti che
        rendono impossibile la verifica.
      </>
    ),
    trovanorma: (
      <>
        <strong>Obbligatoria e precisa.</strong> Ogni risposta ha riferimenti diretti e link alla
        norma o sentenza originale.
      </>
    ),
  },
  {
    criterion: 'Specializzazione',
    generalAI: (
      <>
        <strong>Uso generico.</strong> Non comprende la terminologia e la logica giuridico-fiscale,
        aumentando il rischio di errori di interpretazione.
      </>
    ),
    trovanorma: (
      <>
        <strong>Addestrato per il diritto.</strong> Linguaggio specialistico e analisi pertinenti,
        con contesto e correlazioni corrette.
      </>
    ),
  },
];

const valueProps = [
  'Meno tempo nella ricerca manuale',
  'Sicurezza nell\u2019aggiornamento normativo',
  'Risposte argomentate con fonti verificabili',
];

const pricingFeatures = [
  'Tutte le funzionalità',
  'Utenti e dispositivi illimitati',
  'Assistenza dedicata',
];

const faqData = [
  {
    question: 'Trovanorma sostituisce il lavoro di un professionista?',
    answer:
      'No. Trovanorma supporta il professionista riducendo i tempi di ricerca e formulando pareri documentati, ma la decisione e la strategia finale resta sempre nelle mani del professionista.',
  },
  {
    question: 'In cosa è diverso da ChatGPT o altre AI generaliste?',
    answer:
      'Trovanorma opera su un database legale e normativo proprietario e verificato. Non cerca in Internet, non inventa informazioni e fornisce risposte basate solo su fonti ufficiali.',
  },
  {
    question: 'Quanto tempo impiega a fornire una risposta?',
    answer:
      'Per un parere completo con riferimenti normativi servono in media un paio di minuti. Per ricerche più semplici, la risposta arriva in pochi secondi.',
  },
  {
    question: 'I miei dati e quelli dei miei clienti sono al sicuro?',
    answer:
      'Sì. La sicurezza dei tuoi dati e di quelli dei tuoi clienti è una nostra priorità. Tutti i dati sono crittografati sia in transito che a riposo, siamo pienamente conformi al GDPR e adottiamo misure di sicurezza aggiornate. Le tue ricerche restano completamente private e non vengono mai condivise né utilizzate per addestrare modelli di intelligenza artificiale.',
  },
  {
    question: 'Posso disdire in qualsiasi momento?',
    answer:
      "Sì. Puoi interrompere l'abbonamento in qualsiasi momento: la disattivazione avverrà alla fine del ciclo di fatturazione in corso, senza costi aggiuntivi.",
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AIAvvocatiCommercialistiPage() {
  useSmoothScroll();

  return (
    <div className="min-h-screen overflow-x-hidden">
      <CampaignNavigationBar
        items={[
          { label: 'Come funziona', url: '#come-funziona' },
          { label: 'Prezzi', url: '#prezzi' },
        ]}
      />
      <main className="flex min-h-screen flex-col left-0 right-0">
        <HeroAlt />
        {/*<ReviewCarousel />*/}
        <ProblemStatement />
        <HowItWorksSection />
        <WhatYouCanDoSection />
        {/*<AudienceSection />*/}
        <DifferentiatorSection />
        <CtaMidSection />
        <PricingAlt />
        <FinalCta />
        <FaqAlt />
        <Footer />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function HeroAlt() {
  return (
    <section className="relative px-8 md:px-8 pt-8 bg-cover bg-center bg-[url(/images/bg-wave.svg)]">
      <NavigationBarSpacing />
      <div className="flex flex-col items-center max-w-xl md:max-w-3xl mx-auto">
        <h1 className="font-funnel text-center text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mt-6 md:mt-4 mb-12 leading-tight">
          Intelligenza Artificiale <br />
          per <span className="text-accent">avvocati</span>
        </h1>
        <p className="text-center text-md sm:text-lg lg:text-xl text-neutral-700 max-w-2xl lg:max-w-3xl">
          Analizza norme aggiornate, genera pareri con fonti verificabili e riduce drasticamente il
          tempo di ricerca e redazione.
        </p>
        <div className="flex flex-col items-center mt-12 mb-12 gap-3">
          <Button asChild size="xl" variant="accent-default">
            <Link href={SIGNUP_URL}>
              <Rocket className="w-6 h-6 mr-3" />
              Provalo gratis
            </Link>
          </Button>
          <span className="text-sm font-semibold text-neutral-600">
            Prova gratuita per 14 giorni senza impegno
          </span>
        </div>
        {/*
        <div className="relative mb-8 mt-4 w-full max-w-3xl">
          <div
            className="relative aspect-3/2 md:aspect-auto overflow-hidden rounded-lg cursor-pointer"
            onClick={!videoClicked ? handlePlayClick : undefined}
          >
            {!videoClicked && (
              <>
                <Image
                  src="/videos/hero_cover.webp"
                  alt="Anteprima video Trovanorma"
                  width={1280}
                  height={853}
                  priority
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-accent/90 hover:bg-accent rounded-full p-5 shadow-lg transition duration-200">
                    <Play className="w-10 h-10 text-white fill-white" />
                  </div>
                </div>
              </>
            )}
            <video ref={videoRef} playsInline className={videoClicked ? '' : 'hidden'}>
              <source src="/videos/hero.mp4" type="video/mp4" />
              Il tuo browser non supporta la riproduzione di questo video.
            </video>
          </div>
        </div>
        */}
      </div>

      {/*
      // ---------------------------------------------------------------------------
      // Trust Badges (3 feature cards)
      // ---------------------------------------------------------------------------
      */}
      <div className="max-w-xl md:max-w-4xl mx-auto mt-4 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {trustBadges.map(badge => (
            <div
              key={badge.label}
              className="flex items-center gap-4 bg-white/40 rounded-xl px-6 py-5"
            >
              <badge.icon className="w-6 h-6 min-w-6 text-accent" />
              <span className="text-text-base font-semibold text-base">{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Problem Statement
// ---------------------------------------------------------------------------

function ProblemStatement() {
  return (
    <section className="py-16 px-4 md:px-8 bg-white">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-funnel tracking-tight">
          Quando una norma cambia, <span className="text-accent">cambia tutto</span>
        </h2>
        <div className="text-lg text-neutral-700 mx-auto space-y-4">
          <p>
            Ogni aggiornamento normativo può incidere su pareri, contratti e decisioni operative.
            Rischi di perdere tempo e di dare risposte sbagliate.
          </p>
          <p>
            Trovanorma ti supporta nell&apos;analisi normativa con un sistema di intelligenza
            artificiale progettato per il lavoro professionale.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How It Works
// ---------------------------------------------------------------------------

function HowItWorksSection() {
  return (
    <section id="come-funziona" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 font-funnel tracking-tight">
          Come funziona <span className="text-accent">Trovanorma</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {howItWorks.map(item => (
            <div key={item.step} className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-accent text-white text-xl font-bold mb-4">
                {item.step}
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">{item.title}</h3>
              <p className="text-neutral-600">{item.description}</p>
            </div>
          ))}
        </div>
        <div className="relative mt-12 w-full max-w-3xl mx-auto overflow-hidden rounded-xl border border-neutral-200 shadow-md">
          <Image
            src="/images/how-it-works/screen.webp"
            alt="Interfaccia Trovanorma"
            width={900}
            height={440}
            className="w-full h-auto"
          />
        </div>
        <div className="mt-10">
          <Button asChild size="xl" variant="accent-default">
            <Link href={SIGNUP_URL}>
              <Rocket className="w-6 h-6 mr-3" />
              Attiva ora la prova gratuita
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// WhatYouCanDo Section (3 cards)
// ---------------------------------------------------------------------------

function WhatYouCanDoSection() {
  return (
    <section className="py-16 px-4 md:px-8 bg-white">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 font-funnel tracking-tight">
          Cosa puoi fare con <span className="text-accent">Trovanorma</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {whatYouCanDos.map(a => (
            <div
              key={a.title}
              className="rounded-xl bg-white p-6 shadow-md hover:shadow-lg transition duration-200 border border-neutral-200 text-left"
            >
              <div className="rounded-full h-14 w-14 bg-accent-100 mb-4 relative">
                <a.icon className="w-7 h-7 text-accent absolute mx-auto my-auto top-0 bottom-0 left-0 right-0" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{a.title}</h3>
              <p className="text-sm text-neutral-600">{a.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Differentiator Section
// ---------------------------------------------------------------------------

function DifferentiatorSection() {
  return (
    <section className="py-16 px-4 md:px-8 bg-primary-50">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-funnel tracking-tight">
          Progettata per l&apos;ambito{' '}
          <span className="text-accent whitespace-nowrap">normativo e fiscale</span>
        </h2>
        <p className="text-lg text-neutral-700 mx-auto mb-10">
          A differenza dei sistemi generalisti, Trovanorma è sviluppata per l&apos;analisi normativa
          e professionale, con attenzione a fonti, aggiornamenti e verificabilità delle risposte.
        </p>
        <div className="flex flex-col text-gray-300 overflow-hidden md:bg-white rounded-none md:rounded-xl border-0 md:border border-primary-100 shadow-none md:shadow-md md:hover:shadow-lg transition duration-200">
          <div className="min-w-full align-middle overflow-x-auto">
            {/* Layout flessibile responsivo che sostituisce la tabella */}
            <div className="min-w-full">
              {/* Intestazioni visibili solo su desktop */}
              <div className="hidden md:flex w-full border-b border-neutral-200 bg-white">
                <div className="flex-1 py-3.5 pl-6 pr-3 text-center text-sm font-semibold text-text-base w-1/3"></div>
                <div className="flex-2 px-3 py-3.5 text-center text-md font-semibold text-text-base w-1/3">
                  ChatGPT
                </div>
                <div className="flex-2 px-3 py-3.5 text-center text-md font-semibold text-text-base w-1/3">
                  <Image
                    src="/images/logo-horizontal-orange-blue.svg"
                    alt="Trovanorma"
                    width={140}
                    height={24}
                    className="inline-block"
                  />
                </div>
              </div>

              {/* Corpo: card responsivi per ogni elemento */}
              <div>
                {comparisonData.map(item => (
                  <div
                    key={item.criterion}
                    className="flex flex-col md:flex-row justify-start bg-white md:bg-transparent rounded-xl shadow-sm border border-gray-200 md:shadow-none md:border-none md:border-b md:rounded-none mb-2 md:mb-0"
                  >
                    {/* Criterio */}
                    <div className="flex-1 py-4 pl-6 pr-3 text-base md:text-sm font-semibold text-text-base md:w-1/3 content-center border-b border-neutral-200">
                      {item.criterion}
                    </div>

                    {/* AI Generalista */}
                    <div className="flex-2 px-4 py-4 text-sm text-neutral-600 md:w-1/3 border-l content-center border-b border-neutral-200">
                      <div className="flex items-center gap-2">
                        <ThumbsDown className="text-danger-500 shrink-0 mr-2" size={20} />
                        <span className="text-left">
                          <span className="font-bold text-danger-500 md:hidden">ChatGPT: </span>
                          {item.generalAI}
                        </span>
                      </div>
                    </div>

                    {/* Trovanorma */}
                    <div className="flex-2 px-4 py-4 text-sm text-neutral-600 md:w-1/3 bg-success-500/8 rounded-b-xl md:rounded-none content-center md:border-b border-neutral-200">
                      <div className="flex items-center gap-2">
                        <ThumbsUp className="text-success-500 shrink-0 mr-2" size={20} />
                        <span className="text-left">
                          <span className="text-base font-bold font-funnel text-success-500 md:hidden">
                            Trovanorma:{' '}
                          </span>
                          {item.trovanorma}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CTA Mid-Section (glassmorphism-inspired card)
// ---------------------------------------------------------------------------

function CtaMidSection() {
  return (
    <section className="py-20 px-4 md:px-8 bg-white">
      <div className="max-w-xl md:max-w-2xl mx-auto">
        <div className="backdrop-blur-sm bg-primary-50/80 rounded-2xl p-8 md:py-12 md:px-20 text-center border border-primary-100 shadow-lg">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-funnel tracking-tight">
            <span className="text-accent">Riduci tempi ed errori</span> nella ricerca normativa
          </h2>
          <ul className="space-y-3 mb-8 text-left max-w-md mx-auto">
            {valueProps.map(prop => (
              <li key={prop} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 min-w-5 mt-0.5 md:mt-1 text-primary" />
                <span className="text-neutral-700 text-base md:text-lg">{prop}</span>
              </li>
            ))}
          </ul>
          <Button asChild size="xl" variant="accent-default">
            <Link href={SIGNUP_URL}>
              <Rocket className="w-6 h-6 mr-3" />
              Provalo gratis<span className="hidden sm:inline"> per 14 giorni</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

function PricingAlt() {
  return (
    <section id="prezzi" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 font-funnel tracking-tight">
          Un abbonamento <span className="text-accent">senza limitazioni</span>
        </h2>
        <p className="text-[17px] text-neutral-700 mx-auto mb-12">
          Offriamo soluzioni che si adattano a professionisti, studi e aziende di ogni dimensione: i
          nostri piani hanno utenti e dispositivi illimitati e in ogni momento puoi monitorare il
          numero di quesiti utilizzati dal team.
          <br />
          Puoi cancellare il tuo abbonamento in qualsiasi momento.
        </p>
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 p-8 text-center relative">
            <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl rounded-tr-xl bg-accent text-white text-xs font-semibold">
              PROMO
            </div>
            <h3 className="text-xl font-semibold text-neutral-600 mb-2">
              Abbonamento a partire da
            </h3>
            <div className="flex items-baseline justify-center mb-1">
              <span className="text-5xl font-bold text-primary">€69</span>
              <span className="ml-1 text-lg text-neutral-500">/mese +IVA</span>
            </div>
            <p className="text-sm text-accent font-semibold mb-6">PROMO nuovi clienti</p>
            <ul className="space-y-3 mb-8 text-left">
              {pricingFeatures.map(feature => (
                <li key={feature} className="flex items-start gap-3">
                  <Check className="w-5 h-5 min-w-5 mt-0.5 text-success-500" />
                  <span className="text-neutral-700">{feature}</span>
                </li>
              ))}
            </ul>
            <Button asChild size="lg" variant="accent-default" className="w-full">
              <Link href={SIGNUP_URL}>Provalo gratis per 14 giorni</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final CTA
// ---------------------------------------------------------------------------

function FinalCta() {
  return (
    <section className="py-16 px-4 md:px-8 bg-white">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-funnel tracking-tight">
          Inizia <span className="text-accent">gratis</span>, senza vincoli
        </h2>
        <p className="text-lg text-neutral-700 mx-auto">
          Accedi subito alla piattaforma e verifica concretamente il valore nel tuo lavoro
          quotidiano.
        </p>
        <p className="text-lg text-neutral-700 max-w-2xl mx-auto mb-8">
          Prova gratuita di 14 giorni, senza carta di credito e senza vincoli.
        </p>
        <div>
          <Button asChild size="xl" variant="accent-default">
            <Link href={SIGNUP_URL}>
              <Rocket className="w-6 h-6 mr-3" />
              Attiva ora la prova gratuita
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

function FaqAlt() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, faqData.length);
  }, []);

  return (
    <section id="faq" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary font-funnel tracking-tight mb-6">
          I tuoi dubbi, le nostre <span className="text-accent">risposte</span>
        </h2>
        <p className="text-lg text-neutral-600 mb-12">
          Risposte chiare ai dubbi più comuni, per decidere con tutto ciò di cui hai bisogno.
        </p>
        <div>
          {faqData.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={item.question} className="pt-4">
                <h3>
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full items-center justify-between text-left text-gray-900 cursor-pointer focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <span className="text-base font-medium">{item.question}</span>
                    <span className="ml-6 flex h-7 items-center">
                      <ChevronDown
                        className={`text-accent transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        size={24}
                      />
                    </span>
                  </button>
                </h3>
                <div
                  ref={el => {
                    contentRefs.current[index] = el;
                  }}
                  className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
                  style={{
                    maxHeight: isOpen ? `${contentRefs.current[index]?.scrollHeight}px` : '0px',
                  }}
                >
                  <div className="pt-4 pr-6">
                    <p className="text-base text-left text-gray-600">{item.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
